import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@services/api.ts';
import Bars3Icon from '@/images/bars-3.svg?react';
import LogoutIcon from '@/images/logout.svg?react';
import EllipsisVerticalIcon from '@/images/ellipsis-vertical.svg?react';
import LanguageIcon from '@/images/language.svg?react';
import { useRoleTranslation } from '@hooks/useRoleTransition.ts';
import { useAuth } from '@store/hooks/useAuth.ts';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const { getRoleDisplayName } = useRoleTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    try {
      await authApi.logout();
      logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      logout();
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const getUserDisplayName = () => {
    if (!user) return t('loading', 'Loading...');
    return `${user.first_name} ${user.last_name}`.trim() || user.email;
  };

  const getUserInitials = () => {
    if (!user) return '?';
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  };

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMenu]);

  // Handle mobile logout
  const handleMobileLogout = async () => {
    setShowMobileMenu(false);
    await handleLogout();
  };

  // Handle mobile language toggle
  const handleMobileLanguageToggle = () => {
    setShowMobileMenu(false);
    toggleLanguage();
  };

  return (
    <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 lg:px-8 relative z-40">
      <div className="flex items-center">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200 relative group min-h-[44px] min-w-[44px] flex items-center justify-center"
          title="Open sidebar"
        >
          <Bars3Icon className="h-6 w-6" />
          <span className="sr-only">Open sidebar</span>

          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
            Open sidebar
          </div>
        </button>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center space-x-4">
        <button
          onClick={toggleLanguage}
          className="p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-base-50 transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
          title={t('language', 'Switch Language')}
        >
          <span className="text-sm font-medium">
            {i18n.language === 'en' ? 'العربية' : 'EN'}
          </span>
        </button>
        <div className="flex items-center p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-base-50 transition-colors duration-200">
          <div className="w-8 h-8 bg-primary-700 rounded-full flex items-center justify-center mr-2">
            <span className="text-white text-sm font-medium">
              {getUserInitials()}
            </span>
          </div>
          <div>
            <div className="text-sm font-medium text-text-primary">
              {getUserDisplayName()}
            </div>
            <div className="text-xs text-text-secondary">
              {getRoleDisplayName(user)}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          disabled={isLoggingOut}
          className={`p-2 rounded-md transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center ${
            isLoggingOut
              ? 'opacity-50 cursor-not-allowed text-text-secondary'
              : 'text-text-secondary hover:text-red-600 hover:bg-red-50'
          }`}
          title={t('logout', 'Logout')}
        >
          <LogoutIcon className="w-5 h-5" />
          <span className="sr-only">{t('logout', 'Logout')}</span>
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden flex items-center space-x-2">
        {/* User info visible on mobile */}
        <div className="flex items-center text-sm">
          <div className="w-8 h-8 bg-primary-700 rounded-full flex items-center justify-center mr-2">
            <span className="text-white text-sm font-medium">
              {getUserInitials()}
            </span>
          </div>
          <div className="hidden xs:block">
            <div className="text-sm font-medium text-text-primary truncate max-w-[100px]">
              {getUserDisplayName()}
            </div>
            <div className="text-xs text-text-secondary">
              {getRoleDisplayName(user)}
            </div>
          </div>
        </div>

        {/* Mobile menu button */}
        <div className="relative" ref={mobileMenuRef}>
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-base-50 transition-colors duration-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
            title={t('menu', 'Menu')}
          >
            <EllipsisVerticalIcon className="w-6 h-6" />
          </button>

          {/* Mobile dropdown menu */}
          {showMobileMenu && (
            <div
              className={`absolute top-full mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 ${
                i18n.language === 'ar' ? 'left-0' : 'right-0'
              }`}
            >
              {/* User info in dropdown for extra small screens */}
              <div className="xs:hidden px-4 py-2 border-b border-gray-200">
                <div className="text-sm font-medium text-text-primary">
                  {getUserDisplayName()}
                </div>
                <div className="text-xs text-text-secondary">
                  {getRoleDisplayName(user)}
                </div>
              </div>

              {/* Language switch */}
              <button
                onClick={handleMobileLanguageToggle}
                className={`w-full px-4 py-3 text-sm text-text-secondary hover:text-text-primary hover:bg-base-50 transition-colors duration-200 flex items-center ${
                  i18n.language === 'ar' ? 'text-right' : 'text-left'
                }`}
              >
                <LanguageIcon
                  className={`w-4 h-4 ${i18n.language === 'ar' ? 'ml-3' : 'mr-3'}`}
                />
                {t('language', 'Language')}:{' '}
                {i18n.language === 'en' ? 'العربية' : 'EN'}
              </button>

              {/* Logout */}
              <button
                onClick={handleMobileLogout}
                disabled={isLoggingOut}
                className={`w-full px-4 py-3 text-sm transition-colors duration-200 flex items-center ${
                  i18n.language === 'ar' ? 'text-right' : 'text-left'
                } ${
                  isLoggingOut
                    ? 'opacity-50 cursor-not-allowed text-text-secondary'
                    : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                }`}
              >
                <LogoutIcon
                  className={`w-4 h-4 ${i18n.language === 'ar' ? 'ml-3' : 'mr-3'}`}
                />
                {isLoggingOut
                  ? t('loggingOut', 'Logging out...')
                  : t('logout', 'Logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
